import {
    forwardRef,
    useEffect,
    useImperativeHandle,
    useLayoutEffect,
    useRef,
} from 'react';

import { cn } from '@/lib/utils';

import {
    adjacentBadge,
    badgesMatch,
    BadgeLook,
    buildContent,
    hasRawToken,
    moveToken,
    offsetOfPoint,
    pointOfOffset,
    rangeOfBadge,
    removeRange,
    serialize,
} from './placeholderDom';
import { insertAt } from './alertPlaceholders';

export interface PlaceholderTextFieldHandle {
    /** Puts text (a `{{token}}`) at the caret — or where it last was — and moves the caret behind it. */
    insertText: (text: string, options?: { keepFocus?: boolean }) => void;
    focus: () => void;
}

interface Properties {
    id?: string;
    value: string;
    onChange: (value: string) => void;
    /** A body keeps its newlines; a subject is one line and Enter does nothing. */
    multiline?: boolean;
    /** Label and look of the badge for a token name. */
    describe: (name: string) => BadgeLook;
    labelledBy?: string;
    ariaLabel?: string;
    className?: string;
    invalid?: boolean;
    onFocus?: () => void;
    /** Ctrl/Cmd+J: open the placeholder list for this field. */
    onShortcut?: () => void;
}

interface Snapshot {
    value: string;
    caret: number;
}

/** Marks a drag that carries a placeholder (from the picker or from a badge). */
export const PLACEHOLDER_DRAG_TYPE = 'application/x-tri-placeholder';

/** The badge being dragged out of a field, so the field it lands in knows whether to move it. */
let activeDrag: { root: HTMLElement; range: { start: number; end: number }; handled: boolean } | null = null;

const MAX_HISTORY = 100;
/** Typing within this window is one undo step. */
const COALESCE_MS = 800;

/**
 * A text field in which every `{{token}}` is shown as a small badge.
 *
 * The value is — and stays — the plain string: `onChange` receives exactly the text that is sent and
 * saved, `{{token}}` included. Rendering is a `contenteditable` with non-editable badge spans; all
 * edits that are not plain typing (Enter, paste, backspace on a badge, undo, insert from the picker)
 * go through the string and rebuild the DOM, so the field is controlled like an input and the caret
 * is restored by its offset in the string. While an IME composes, nothing is rebuilt.
 *
 * The serialized value is also published as `data-value` for tests and tooling.
 */
const PlaceholderTextField = forwardRef<PlaceholderTextFieldHandle, Properties>((properties, reference) => {
    const { value, onChange, multiline = false, describe } = properties;
    const element = useRef<HTMLDivElement>(null);
    const composing = useRef(false);
    const suppressFocus = useRef(false);
    // Where the caret / selection is, in string offsets; kept while the focus is elsewhere (picker).
    const lastSelection = useRef<{ start: number; end: number } | null>(null);
    // Caret to set after the next rebuild.
    const pendingSelection = useRef<{ start: number; end: number } | null>(null);
    const describeReference = useRef(describe);
    const history = useRef<{ stack: Snapshot[]; index: number; time: number }>({ stack: [], index: -1, time: 0 });

    describeReference.current = describe;

    const currentSelection = (): { start: number; end: number } | null => {
        const root = element.current;
        const selection = root?.ownerDocument.getSelection();

        if (!root || !selection || selection.rangeCount === 0 || !root.contains(selection.anchorNode)
            || !root.contains(selection.focusNode)) {
            return null;
        }

        const anchor = offsetOfPoint(root, selection.anchorNode as Node, selection.anchorOffset);
        const focus = offsetOfPoint(root, selection.focusNode as Node, selection.focusOffset);

        return { start: Math.min(anchor, focus), end: Math.max(anchor, focus) };
    };

    const applySelection = (range: { start: number; end: number }) => {
        const root = element.current;
        const selection = root?.ownerDocument.getSelection();

        if (!root || !selection) {
            return;
        }

        const from = pointOfOffset(root, range.start);
        const to = range.end === range.start ? from : pointOfOffset(root, range.end);

        try {
            selection.setBaseAndExtent(from.node, from.offset, to.node, to.offset);
        } catch {
            // A detached or foreign node: leave the caret where the browser has it.
        }
    };

    const record = (snapshot: Snapshot, force: boolean) => {
        const state = history.current;
        const top = state.stack[state.index];

        if (top && top.value === snapshot.value) {
            return;
        }

        const now = Date.now();

        if (!force && top && now - state.time < COALESCE_MS && state.index === state.stack.length - 1 && state.index > 0) {
            state.stack[state.index] = snapshot;
        } else {
            state.stack = [...state.stack.slice(0, state.index + 1), snapshot].slice(-MAX_HISTORY);
            state.index = state.stack.length - 1;
        }

        state.time = now;
    };

    if (history.current.index === -1) {
        history.current = { stack: [{ value, caret: value.length }], index: 0, time: 0 };
    }

    /** Replaces the range [start, end) of the string and puts the caret behind the new text. */
    const edit = (text: string, start: number, end: number) => {
        const inserted = insertAt(value, text, start, end);

        pendingSelection.current = { start: inserted.caret, end: inserted.caret };
        lastSelection.current = pendingSelection.current;
        record({ value: inserted.value, caret: inserted.caret }, true);
        onChange(inserted.value);
    };

    const editSelection = (text: string) => {
        const range = currentSelection() ?? lastSelection.current ?? { start: value.length, end: value.length };

        edit(text, range.start, range.end);
    };

    useImperativeHandle(reference, () => ({
        insertText: (text: string, options) => {
            editSelection(text);

            // From a list that stays open the focus must stay there.
            suppressFocus.current = !!options?.keepFocus;

            if (!options?.keepFocus) {
                element.current?.focus();
            }
        },
        focus: () => element.current?.focus(),
    }));

    // Keep the DOM in step with the value: rebuild when the value was changed from outside or typing
    // completed a token (or a badge's label changed); leave it alone while the user is typing.
    useLayoutEffect(() => {
        const root = element.current;

        if (!root || composing.current) {
            return;
        }

        const inSync = serialize(root) === value && !hasRawToken(root) && badgesMatch(root, describeReference.current);

        if (inSync && !pendingSelection.current) {
            return;
        }

        const focused = root.ownerDocument.activeElement === root;
        const keep = pendingSelection.current ?? (focused ? currentSelection() : null);

        pendingSelection.current = null;

        if (!inSync) {
            buildContent(root, value, (name) => describeReference.current(name), multiline);
        }

        if (keep) {
            lastSelection.current = keep;

            // Setting a selection in an editable moves the focus there; skip that while the list is in use.
            if (focused || !suppressFocus.current) {
                applySelection(keep);
            }
        }

        suppressFocus.current = false;
    });

    // The selection survives a blur (the picker takes the focus): remember it.
    useEffect(() => {
        const document_ = element.current?.ownerDocument;

        if (!document_) {
            return undefined;
        }

        const remember = () => {
            const range = currentSelection();

            if (range) {
                lastSelection.current = range;
            }
        };

        document_.addEventListener('selectionchange', remember);

        return () => document_.removeEventListener('selectionchange', remember);
    }, []);

    const emitFromDom = () => {
        const root = element.current;

        if (!root) {
            return;
        }

        const next = serialize(root);
        const range = currentSelection();

        if (range) {
            // Typing completed a token: the rebuild must put the caret behind the new badge.
            pendingSelection.current = hasRawToken(root) ? range : null;
            lastSelection.current = range;
        }

        record({ value: next, caret: range?.end ?? next.length }, false);

        if (next !== value) {
            onChange(next);
        } else if (hasRawToken(root)) {
            // Value unchanged but the DOM holds a raw token: the layout effect only runs on render.
            buildContent(root, value, (name) => describeReference.current(name), multiline);

            if (range) {
                applySelection(range);
            }
        }
    };

    const restore = (index: number) => {
        const state = history.current;
        const snapshot = state.stack[index];

        if (!snapshot) {
            return;
        }

        state.index = index;
        pendingSelection.current = { start: snapshot.caret, end: snapshot.caret };
        onChange(snapshot.value);
    };

    /** Takes the badge whose (x) was used out of the string; the caret stays where the badge was. */
    const removeBadgeOf = (target: EventTarget | null): boolean => {
        const root = element.current;
        const badge = (target as HTMLElement | null)?.closest?.('[data-remove]')?.closest('[data-raw]') as HTMLElement | null;

        if (!root || !badge || !root.contains(badge)) {
            return false;
        }

        const range = rangeOfBadge(root, badge);

        edit('', range.start, range.end);
        root.focus();

        return true;
    };

    const onKeyDown = (event: React.KeyboardEvent<HTMLDivElement>) => {
        if ((event.target as HTMLElement).closest?.('[data-remove]')) {
            if (event.key === 'Enter' || event.key === ' ') {
                event.preventDefault();
                removeBadgeOf(event.target);
            }

            return;
        }

        if (composing.current || event.nativeEvent.isComposing) {
            return;
        }

        const modifier = event.metaKey || event.ctrlKey;

        if (modifier && !event.altKey && event.key.toLowerCase() === 'z') {
            event.preventDefault();
            restore(event.shiftKey ? history.current.index + 1 : history.current.index - 1);

            return;
        }

        if (modifier && !event.altKey && event.key.toLowerCase() === 'j' && properties.onShortcut) {
            event.preventDefault();
            properties.onShortcut();

            return;
        }

        if (modifier && !event.altKey && event.key.toLowerCase() === 'y') {
            event.preventDefault();
            restore(history.current.index + 1);

            return;
        }

        if (event.key === 'Enter') {
            event.preventDefault();

            if (multiline) {
                editSelection('\n');
            }

            return;
        }

        const root = element.current;
        const range = currentSelection();

        if (!root || !range || range.start !== range.end || event.shiftKey || modifier) {
            return;
        }

        const direction = event.key === 'Backspace' || event.key === 'ArrowLeft'
            ? 'before'
            : event.key === 'Delete' || event.key === 'ArrowRight'
                ? 'after'
                : null;

        if (!direction) {
            return;
        }

        const badge = adjacentBadge(root, range.start, direction);

        if (!badge) {
            return;
        }

        event.preventDefault();

        if (event.key === 'Backspace' || event.key === 'Delete') {
            edit('', badge.start, badge.end);
        } else {
            const target = direction === 'before' ? badge.start : badge.end;

            applySelection({ start: target, end: target });
            lastSelection.current = { start: target, end: target };
        }
    };

    const onPaste = (event: React.ClipboardEvent<HTMLDivElement>) => {
        event.preventDefault();

        const pasted = event.clipboardData.getData('text/plain').replace(/\r\n?/g, '\n');

        editSelection(multiline ? pasted : pasted.replace(/\n+/g, ' '));
    };


    /** The string offset under a pointer; a point inside a badge snaps to the nearer side. */
    const offsetAtPoint = (x: number, y: number): number => {
        const root = element.current;
        const document_ = root?.ownerDocument as Document & {
            caretPositionFromPoint?: (x: number, y: number) => { offsetNode: Node; offset: number } | null;
            caretRangeFromPoint?: (x: number, y: number) => Range | null;
        };

        if (!root) {
            return value.length;
        }

        let node: Node | null = null;
        let offset = 0;

        if (document_.caretPositionFromPoint) {
            const position = document_.caretPositionFromPoint(x, y);

            node = position?.offsetNode ?? null;
            offset = position?.offset ?? 0;
        } else if (document_.caretRangeFromPoint) {
            const range = document_.caretRangeFromPoint(x, y);

            node = range?.startContainer ?? null;
            offset = range?.startOffset ?? 0;
        }

        if (!node || !root.contains(node)) {
            return value.length;
        }

        const badge = (node.nodeType === 1 ? (node as HTMLElement) : node.parentElement)?.closest('[data-raw]');

        if (badge && root.contains(badge)) {
            const box = badge.getBoundingClientRect();
            const range = rangeOfBadge(root, badge as HTMLElement);

            return x < box.left + box.width / 2 ? range.start : range.end;
        }

        return offsetOfPoint(root, node, offset);
    };

    const carriesPlaceholder = (event: React.DragEvent<HTMLDivElement>): boolean =>
        Array.from(event.dataTransfer.types).some((type) => type === PLACEHOLDER_DRAG_TYPE || type === 'text/plain');

    const onDragStart = (event: React.DragEvent<HTMLDivElement>) => {
        if ((event.target as HTMLElement).closest?.('[data-remove]')) {
            event.preventDefault();

            return;
        }

        const root = element.current;
        const badge = (event.target as HTMLElement).closest?.('[data-raw]') as HTMLElement | null;

        if (!root || !badge || !root.contains(badge)) {
            return;
        }

        const raw = badge.getAttribute('data-raw') ?? '';

        activeDrag = { root, range: rangeOfBadge(root, badge), handled: false };
        root.setAttribute('data-dragging', '');
        event.dataTransfer.effectAllowed = 'copyMove';
        event.dataTransfer.setData(PLACEHOLDER_DRAG_TYPE, raw);
        event.dataTransfer.setData('text/plain', raw);
    };

    const onDragOver = (event: React.DragEvent<HTMLDivElement>) => {
        if (!carriesPlaceholder(event)) {
            return;
        }

        event.preventDefault();
        event.dataTransfer.dropEffect = activeDrag && !event.altKey ? 'move' : 'copy';

        // The caret follows the pointer: it is the insertion line.
        const target = offsetAtPoint(event.clientX, event.clientY);

        applySelection({ start: target, end: target });
    };

    const onDrop = (event: React.DragEvent<HTMLDivElement>) => {
        if (!carriesPlaceholder(event)) {
            return;
        }

        event.preventDefault();

        const target = offsetAtPoint(event.clientX, event.clientY);
        const dropped = event.dataTransfer.getData('text/plain').replace(/\r\n?/g, '\n');
        const text = multiline ? dropped : dropped.replace(/\n+/g, ' ');

        if (activeDrag && activeDrag.root === element.current && event.dataTransfer.types.includes(PLACEHOLDER_DRAG_TYPE)) {
            const result = moveToken(value, activeDrag.range, target, event.altKey);

            activeDrag.handled = true;
            pendingSelection.current = { start: result.caret, end: result.caret };
            record({ value: result.value, caret: result.caret }, true);
            onChange(result.value);

            return;
        }

        edit(text, target, target);
        element.current?.focus();
    };

    const onDragEnd = (event: React.DragEvent<HTMLDivElement>) => {
        const drag = activeDrag;

        activeDrag = null;
        element.current?.removeAttribute('data-dragging');

        // A move into another field: this field gives the badge up (a drop in this field did it already).
        if (drag && drag.root === element.current && !drag.handled && event.dataTransfer.dropEffect === 'move') {
            const next = removeRange(value, drag.range);

            record({ value: next, caret: drag.range.start }, true);
            onChange(next);
        }
    };

    const onBeforeInput = (event: React.FormEvent<HTMLDivElement>) => {
        const type = (event.nativeEvent as InputEvent).inputType;

        // Enter / paste / undo are handled above; keep the browser from adding its own blocks or history.
        if (type === 'insertParagraph' || type === 'insertLineBreak') {
            event.preventDefault();
        } else if (type === 'historyUndo' || type === 'historyRedo') {
            event.preventDefault();
        }
    };

    return (
        <div
            id={properties.id}
            ref={element}
            role="textbox"
            tabIndex={0}
            contentEditable
            suppressContentEditableWarning
            spellCheck
            aria-multiline={multiline}
            aria-labelledby={properties.labelledBy}
            aria-label={properties.labelledBy ? undefined : properties.ariaLabel}
            aria-invalid={properties.invalid || undefined}
            data-value={value}
            className={cn(
                'w-full min-w-0 rounded-md border border-input bg-transparent px-3 py-1.5 text-sm shadow-xs outline-none',
                'focus-visible:border-ring focus-visible:ring-[3px] focus-visible:ring-ring/50',
                'aria-invalid:border-destructive',
                multiline
                    ? 'min-h-24 whitespace-pre-wrap break-words'
                    : 'block min-h-9 overflow-x-auto whitespace-nowrap leading-6 max-sm:min-h-11 max-sm:py-2.5',
                properties.className,
            )}
            onInput={() => {
                if (!composing.current) {
                    emitFromDom();
                }
            }}
            onCompositionStart={() => { composing.current = true; }}
            onCompositionEnd={() => {
                composing.current = false;
                emitFromDom();
            }}
            onKeyDown={onKeyDown}
            onPaste={onPaste}
            onBeforeInput={onBeforeInput}
            onMouseDown={(event) => {
                // The (x) must not move the caret or start a text selection.
                if ((event.target as HTMLElement).closest?.('[data-remove]')) {
                    event.preventDefault();
                }
            }}
            onClick={(event) => {
                removeBadgeOf(event.target);
            }}
            onDragStart={onDragStart}
            onDragOver={onDragOver}
            onDrop={onDrop}
            onDragEnd={onDragEnd}
            onFocus={properties.onFocus}
        />
    );
});

PlaceholderTextField.displayName = 'PlaceholderTextField';

export default PlaceholderTextField;
